import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useTesterRerunsRequired } from '../../../hooks/analytics/useTesterReports.ts';
import { WidgetCard, formatDate, StatusChip } from '../controller/shared.tsx';

/**
 * Report #35 in the backend file (docx report #36) — Re-runs Required.
 * Amber alert list of the caller's controls flagged for re-run, each with a
 * "Start Re-run" affordance linking into the existing run-test flow
 * (`/sox-controls/:id/run-test`).
 */
export const RerunsRequiredAlert: React.FC<{ controlIdFilter?: Set<string> | null; assigneeUserId?: number | null }> = ({
  controlIdFilter,
  assigneeUserId,
}) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useTesterRerunsRequired(assigneeUserId);
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  return (
    <WidgetCard
      title="Re-runs Required"
      description="Your controls flagged for a re-test or roll-forward"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load re-runs required.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No re-runs required — you're all caught up."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <ul className="analytics-scroll space-y-3 max-h-96 overflow-y-auto">
        {rows.map((row, idx) => (
          <li
            key={`${row.control_id}-${idx}`}
            className="border border-amber-200 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
          >
            <RefreshCw className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-medium text-gray-900 truncate">
                  {row.control_id} — {row.title}
                </p>
                <StatusChip value={row.workflow_status} />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {row.rerun_reason ?? 'No reason recorded'}
                {row.created_at ? ` · flagged ${formatDate(row.created_at)}` : ''}
              </p>
              <Link
                to={`/sox-controls/${encodeURIComponent(row.control_id)}/run-test`}
                className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Start Re-run
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
};

export default RerunsRequiredAlert;
